/**
 * Visit dates are plain `YYYY-MM-DD`, parsed as local calendar dates.
 * `new Date('2026-09-14')` parses as UTC midnight and shows the previous day
 * west of Greenwich — a visit dated a day early in a clinical record.
 *
 * Every format here is built by hand rather than handed to `Intl` with an
 * undefined locale. A clinical record must read the same on every machine, and
 * a developer laptop set to en-US was rendering "Sep 25, 2026" while the clinic
 * writes 25/09.
 */

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Monday first: the clinic's week starts when the clinic opens. */
export const WEEKDAY_INITIALS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function parseCalendarDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
}

export function toIso(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** "25 Sep 2026". */
export function formatVisitDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = parseCalendarDate(iso)
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
}

export function formatLastVisit(iso: string | null | undefined): string {
  if (!iso) return 'No visits yet'
  return formatVisitDate(iso)
}

/** "25/09/2026" — what the doctor types and reads in a field. */
export function formatDateInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const [year, month, day] = iso.split('-')
  if (!year || !month || !day) return ''
  return `${day}/${month}/${year}`
}

/**
 * A month by name, case-insensitively: "sep", "Sep", "SEPT", "september" are
 * all month 8. Nobody types a month the same way twice, and a date field that
 * only answers to one casing is a field that looks broken.
 *
 * Fewer than three letters, or a prefix that fits two months ("ju"), is not an
 * answer — guessing between June and July in a clinical record is not a
 * convenience.
 */
export function monthFromName(text: string): number | null {
  const key = text.trim().toLowerCase()
  if (key.length < 3) return null
  const hits = MONTHS_LONG.filter((month) => month.toLowerCase().startsWith(key))
  return hits.length === 1 ? MONTHS_LONG.indexOf(hits[0]!) : null
}

/**
 * A typed date back to ISO, or null if it is not a real date.
 *
 * Day first, the way the clinic writes it, and forgiving about how it is
 * separated: "25/09/2026", "25-09-2026", "25.9.2026", "25 Sep 2026",
 * "25september2026" and "25092026" are the same day. A month *name* also reads
 * the other way round ("Sep 25 2026") because a name cannot be mistaken for a
 * day — unlike 09/10, which is why the numeric form stays strictly dd/mm.
 *
 * The year must be four digits: "25/09/26" is a 1926 birth date as easily as a
 * 2026 one, and a clinic is exactly where both are plausible.
 *
 * Checked by round-trip rather than by range: 31/02 parses to 3 March, and a
 * date field that silently accepts the wrong day is worse than one that refuses.
 */
export function parseDateInput(text: string): string | null {
  const raw = text.trim()
  // Digits, letters and the punctuation people put between them — nothing else.
  if (!raw || !/^[\dA-Za-z\s/.,-]+$/.test(raw)) return null

  const tokens = raw.match(/\d+|[A-Za-z]+/g) ?? []
  let day: number
  let month: number
  let year: number

  if (tokens.length === 1 && /^\d{8}$/.test(tokens[0]!)) {
    const digits = tokens[0]!
    day = Number(digits.slice(0, 2))
    month = Number(digits.slice(2, 4))
    year = Number(digits.slice(4, 8))
  } else if (tokens.length === 3) {
    const yearAt = tokens.findIndex((token) => /^\d{4}$/.test(token))
    if (yearAt < 0) return null
    year = Number(tokens[yearAt])

    const rest = tokens.filter((_, index) => index !== yearAt)
    const nameAt = rest.findIndex((token) => /[A-Za-z]/.test(token))
    if (nameAt < 0) {
      if (!rest.every((token) => /^\d{1,2}$/.test(token))) return null
      day = Number(rest[0])
      month = Number(rest[1])
    } else {
      const named = monthFromName(rest[nameAt]!)
      if (named === null) return null
      month = named + 1
      const dayToken = rest[1 - nameAt]!
      if (!/^\d{1,2}$/.test(dayToken)) return null
      day = Number(dayToken)
    }
  } else {
    return null
  }

  if (month < 1 || month > 12) return null
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return toIso(date)
}

/** ISO dates sort as text, which is the whole point of the format. */
export function isDateInRange(iso: string, min?: string | undefined, max?: string | undefined): boolean {
  if (min && iso < min) return false
  if (max && iso > max) return false
  return true
}

export function todayIso(now: Date = new Date()): string {
  return toIso(now)
}

export function formatTimeOfDay(iso: string): string {
  const date = new Date(iso)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/** "2 Sep" — a chart axis has no room for a year, and every tick shares one. */
export function formatAxisDate(iso: string): string {
  const date = parseCalendarDate(iso)
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`
}

/** "Wed, 2 Sep" — for a tooltip, where the weekday is the point of a clinic
 *  attendance chart: a trough on a Sunday is not a trough. */
export function formatPointDate(iso: string): string {
  const date = parseCalendarDate(iso)
  return `${WEEKDAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]}`
}

/** "September 2026", for a calendar header. */
export function formatMonthYear(date: Date): string {
  return `${MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}`
}

/**
 * "25 Sep 2026", or "25–28 Sep 2026" for a span.
 *
 * Collapses what the two ends share, because "25 Sep 2026 – 28 Sep 2026" is
 * mostly the same characters twice and a list of trips is read at a glance.
 */
export function formatDateRange(from: string, to: string): string {
  if (!from) return ''
  if (!to || to === from) return formatVisitDate(from)
  const start = parseCalendarDate(from)
  const end = parseCalendarDate(to)
  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = sameYear && start.getMonth() === end.getMonth()
  if (sameMonth) {
    return `${start.getDate()}–${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`
  }
  if (sameYear) {
    return `${start.getDate()} ${MONTHS[start.getMonth()]} – ${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`
  }
  return `${formatVisitDate(from)} – ${formatVisitDate(to)}`
}

/** "1 day late", "6 days late". Singular matters: a badge reading "1 days
 *  late" on a clinical screen reads as a bug and costs the rest of the screen
 *  its credibility. */
export function formatDaysLate(days: number): string {
  return `${days} ${days === 1 ? 'day' : 'days'} late`
}
